import classNames from 'classnames';
import s from './DateInput.module.scss';
import {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import Calendar from '@/shared/assets/icons/calendar.svg';
import { IMaskInput } from 'react-imask';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { useDispatch } from 'react-redux';
import { alertClose } from '@/entities/Alert';

interface InputProps {
  className?: string;
  placeholder?: string;
  error: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement> | string) => void;
  disabled?: boolean;
  onBlur?: () => void;
  name?: string;
  fullWidth?: boolean;
  inputRadius?: boolean;
}

type TriggerEvent = ReactMouseEvent<Element> | ReactTouchEvent<Element>;

export const DateInput = (props: InputProps) => {
  const {
    className,
    placeholder,
    error,
    value = '',
    onChange,
    disabled,
    onBlur,
    name,
    fullWidth = false,
    inputRadius = false,
  } = props;

  const onAlert = useAlert();
  const dispatch = useDispatch();
  const mods = {
    [s.disabled]: disabled,
    [s.isError]: !!error,
  };

  const nativeInputRef = useRef<HTMLInputElement | null>(null);

  const nativeValue = useMemo(() => {
    if (!value || value.length !== 10) return '';
    const [dd, mm, yyyy] = value.split('.');
    if (!dd || !mm || !yyyy) return '';
    return `${yyyy}-${mm}-${dd}`;
  }, [value]);

  useEffect(() => {
    const trimmed = (error ?? '').trim();
    if (trimmed.length > 0) {
      onAlert(error, true);
    } else {
      dispatch(alertClose());
    }
  }, [error]);

  useEffect(() => {
    if (nativeInputRef.current) {
      nativeInputRef.current.value = nativeValue;
    }
  }, [nativeValue]);

  useEffect(() => {
    const el = nativeInputRef.current;
    if (!el) return;

    const onNativeChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const raw = target.value;

      if (!raw) {
        onChange('');
        if (onBlur) onBlur();
        return;
      }

      const [yyyy, mm, dd] = raw.split('-');
      if (!yyyy || !mm || !dd) {
        onChange('');
        if (onBlur) onBlur();
        return;
      }

      onChange(`${dd}.${mm}.${yyyy}`);
      if (onBlur) onBlur();
    };

    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('change', onNativeChange);
    };
  }, [onChange, onBlur]);

  const handleMaskAccept = (val: string | number | undefined) => {
    if (typeof val === 'string') {
      onChange(val);
    }
  };

  const triggerPicker = (event?: TriggerEvent) => {
    if (disabled) return;
    event?.preventDefault();
    nativeInputRef.current?.showPicker();
  };

  return (
    <div className={classNames(s.Input, mods, { [s.fullWidth]: fullWidth, [s.inputRadius]: inputRadius }, [className])}>
      <IMaskInput
        className={classNames(s.input, [className])}
        value={value}
        mask={Date}
        pattern={'d.m`.`Y'}
        overwrite={true}
        autofix={'pad'}
        onAccept={handleMaskAccept}
        placeholder={placeholder}
        type={'text'}
        disabled={disabled}
        onBlur={onBlur}
        name={name}
      />
      <Calendar
        className={s.icon}
        onMouseDown={triggerPicker}
        onTouchStart={triggerPicker}
        onClick={(event) => event.preventDefault()}
      />
      <input
        ref={nativeInputRef}
        type="date"
        aria-hidden
        tabIndex={-1}
        className={s.nativeInput}
        defaultValue={nativeValue}
      />
    </div>
  );
};
